import { useQuery } from "@tanstack/react-query";
import type { TApiODIN, TMultipropDeal } from "@/apis/odin/apiODIN";
import { projectKeys } from "@/queries/queryKeys";
import { createSelectorFilteredDealFiles } from "@/store/deals/createFilteredDealFiles";
import { transformFileView } from "@/store/deals/transformFileView";

export const createProjectQueryHooks = ({ apiODIN }: { apiODIN: TApiODIN }) => {
    const useProjectsQuery = (databaseIds: number[]) =>
        useQuery({
            queryKey: projectKeys.list(databaseIds),
            queryFn: async () => {
                const response = await apiODIN.getAllDeals(databaseIds);
                return response.data.deals;
            },
            enabled: databaseIds.length > 0,
        });

    const getProjectDetailQueryOptions = (
        projectId: number,
        databaseId: number,
    ) => ({
        queryKey: projectKeys.detail(projectId, databaseId),
        queryFn: async () => {
            const response = await apiODIN.getDeal({
                projectId,
                databaseId,
            });
            return response.data;
        },
    });

    const useProjectDetailQuery = (
        projectId: number,
        databaseId: number,
        options?: { enabled?: boolean },
    ) =>
        useQuery({
            ...getProjectDetailQueryOptions(projectId, databaseId),
            enabled: options?.enabled,
        });

    const useProjectFilesQuery = (
        projectId: number,
        databaseId: number,
        options?: { enabled?: boolean },
    ) =>
        useQuery({
            queryKey: projectKeys.files(projectId, databaseId),
            queryFn: async () => {
                const response = await apiODIN.getDealFiles({
                    projectId,
                    databaseId,
                });
                const filteredFiles = createSelectorFilteredDealFiles({
                    dealFiles: response.data.files,
                });
                return {
                    filteredFiles,
                    transformedFileView: transformFileView(filteredFiles),
                    allFiles: response.data.files,
                };
            },
            enabled: options?.enabled,
        });

    const usePropertyInfoForDeleteQuery = (
        propertyId: number | null,
        databaseId: number | null,
        projectId: number | null,
        options?: { enabled?: boolean },
    ) => {
        const ready =
            propertyId !== null && databaseId !== null && projectId !== null;
        return useQuery({
            queryKey: projectKeys.propertyInfoForDelete(
                propertyId ?? -1,
                databaseId ?? -1,
                projectId ?? -1,
            ),
            queryFn: async () => {
                if (!ready) throw new Error("missing ids");
                const response = await apiODIN.getPropertyInfoForDelete({
                    propertyId,
                    databaseId,
                    projectId,
                });
                return { ...response.data, propertyId };
            },
            enabled: (options?.enabled ?? true) && ready,
        });
    };

    const useMultipropDealQuery = (
        projectId: number,
        databaseId: number,
        options?: { enabled?: boolean },
    ) =>
        useQuery<TMultipropDeal>({
            queryKey: projectKeys.multipropDetail(projectId, databaseId),
            queryFn: async () => {
                const response = await apiODIN.multipropGetDeal({
                    projectId,
                    databaseId,
                });
                return response.data;
            },
            enabled: options?.enabled,
        });

    return {
        useProjectsQuery,
        useProjectDetailQuery,
        useProjectFilesQuery,
        usePropertyInfoForDeleteQuery,
        useMultipropDealQuery,
        getProjectDetailQueryOptions,
    };
};

export type TProjectQueryHooks = ReturnType<typeof createProjectQueryHooks>;
